import classNames from 'classnames';
import { useEffect, useRef } from 'react';
import { Picture, PictureProps, RichText } from 'ui/components';
import styles from './Parallax.module.scss';

export interface ParallaxProps {
	className?: string;
	image?: PictureProps;
	text?: string;
	removeAlt?: boolean;
	backgroundColor?: string;
	textColor?: string;
	textAlignment?: Content.ParallaxTextAlignment;
	boxPosition?: Content.ParallaxBoxPosition;
}

const positionClassMap: Record<Content.ParallaxBoxPosition, string> = {
	'Top Left': styles.Parallax_content___topLeft,
	'Top Center': styles.Parallax_content___topCenter,
	'Top Right': styles.Parallax_content___topRight,
	'Center Left': styles.Parallax_content___centerLeft,
	'Center': styles.Parallax_content___center,
	'Center Right': styles.Parallax_content___centerRight,
	'Bottom Left': styles.Parallax_content___bottomLeft,
	'Bottom Center': styles.Parallax_content___bottomCenter,
	'Bottom Right': styles.Parallax_content___bottomRight,
};

const alignmentClassMap: Record<Content.ParallaxTextAlignment, string> = {
	'Left': styles.Parallax_textBox___left,
	'Center': styles.Parallax_textBox___center,
	'Right': styles.Parallax_textBox___right,
};

export const Parallax: React.FC<ParallaxProps> = ({
	className,
	image,
	text,
	removeAlt,
	backgroundColor,
	textColor,
	textAlignment = 'Left',
	boxPosition = 'Center',
}) => {
	const parallaxRef = useRef<HTMLDivElement>(null);
	const imageLayerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const parallaxElement = parallaxRef.current;
		const imageLayer = imageLayerRef.current;

		if (!parallaxElement || !imageLayer) {
			return;
		}

		const reducedMotionQuery = window.matchMedia(
			'(prefers-reduced-motion: reduce)'
		);

		let animationFrameId: number | null = null;

		const updateParallax = () => {
			animationFrameId = null;

			if (reducedMotionQuery.matches) {
				imageLayer.style.transform = 'translate3d(0, 0, 0)';
				return;
			}

			const rect = parallaxElement.getBoundingClientRect();
			const viewportHeight = window.innerHeight;

			if (rect.bottom < 0 || rect.top > viewportHeight) {
				return;
			}

			const viewportCenter = viewportHeight / 2;
			const elementCenter = rect.top + rect.height / 2;

			const distanceFromCenter =
				viewportCenter - elementCenter;

			// This decides how much the Parallax image is allowed to move
			const maxOffset = rect.height * 0.46;

			// This determines the Parallax strength/speed factor
			const offset = Math.max(
				-maxOffset,
				Math.min(maxOffset, distanceFromCenter * 0.30)
			);

			imageLayer.style.transform =
				`translate3d(0, ${offset}px, 0)`;
		};

		const requestParallaxUpdate = () => {
			if (animationFrameId !== null) {
				return;
			}

			animationFrameId =
				window.requestAnimationFrame(updateParallax);
		};

		requestParallaxUpdate();

		window.addEventListener(
			'scroll',
			requestParallaxUpdate,
			{ passive: true }
		);

		window.addEventListener(
			'resize',
			requestParallaxUpdate
		);

		reducedMotionQuery.addEventListener(
			'change',
			requestParallaxUpdate
		);

		return () => {
			window.removeEventListener(
				'scroll',
				requestParallaxUpdate
			);

			window.removeEventListener(
				'resize',
				requestParallaxUpdate
			);

			reducedMotionQuery.removeEventListener(
				'change',
				requestParallaxUpdate
			);

			if (animationFrameId !== null) {
				window.cancelAnimationFrame(animationFrameId);
			}
		};
	}, []);

	if (!image) {
		return null;
	}

	const resolvedBackgroundColor =
		backgroundColor || '#FFFFFF';

	const resolvedTextColor =
		textColor || '#000000';

	const positionClass =
		positionClassMap[boxPosition];

	const alignmentClass =
		alignmentClassMap[textAlignment];

	const focalPointX =
		(image.focalPoint?.left ?? 0.5) * 100;

	const focalPointY =
		(image.focalPoint?.top ?? 0.5) * 100;

	const imageStyle = {
		'--parallax-focal-x': `${focalPointX}%`,
		'--parallax-focal-y': `${focalPointY}%`,
	} as React.CSSProperties;

	return (
		<div
			ref={parallaxRef}
			className={classNames(
				styles.Parallax,
				className
			)}
		>
			<div
				ref={imageLayerRef}
				className={styles.Parallax_imageLayer}
				style={imageStyle}
			>
				<Picture
					{...image}
					removeAlt={removeAlt}
					sizes="100vw"
					className={styles.Parallax_picture}
				/>
			</div>

			{text && (
				<div
					className={classNames(
						styles.Parallax_content,
						positionClass
					)}
				>
					<div
						className={classNames(
							styles.Parallax_textBox,
							alignmentClass
						)}
						style={{
							backgroundColor:
								resolvedBackgroundColor,
						}}
					>
						<RichText
							content={text}
							textColor={resolvedTextColor}
						/>
					</div>
				</div>
			)}
		</div>
	);
};